// Per-terminal colour assignment. Persisted on the backend via /api/settings
// (was localStorage["portColors"] pre-Bun-port). The cache layer keeps the
// load/save API synchronous so the calendar store can initialise during
// render — the first fetch happens in App.tsx onMount.

import { getCache, setPortColors } from "../../shared/settings/cache";

// Curated light terminal palette. Single source of truth: each entry
// carries a human name (for picker tooltips), the tailwind-200 bar
// fill, and the tailwind-700 stripe companion. Light bars + same-hue
// darker stripe is the design language; users pick from this fixed
// set so each (bar, stripe) pair is harmonious.
//
// Listed in colour-wheel order — the picker grid (COLOR_PALETTE_BY_HUE)
// renders straight from this; COLOR_PALETTE re-orders into a warm/cool
// interleave so nextColor()'s first auto-assignments look distinct.
export interface PaletteEntry {
  name: string;
  hex: string;     // tailwind-200 — bar fill
  stripe: string;  // tailwind-700 — stevedore stripe
}
const PALETTE: readonly PaletteEntry[] = [
  { name: "Red",     hex: "#fecaca", stripe: "#b91c1c" },
  { name: "Orange",  hex: "#fed7aa", stripe: "#c2410c" },
  { name: "Amber",   hex: "#fde68a", stripe: "#b45309" },
  { name: "Yellow",  hex: "#fef08a", stripe: "#a16207" },
  { name: "Lime",    hex: "#d9f99d", stripe: "#4d7c0f" },
  { name: "Green",   hex: "#bbf7d0", stripe: "#15803d" },
  { name: "Emerald", hex: "#a7f3d0", stripe: "#047857" },
  { name: "Teal",    hex: "#99f6e4", stripe: "#0f766e" },
  { name: "Cyan",    hex: "#a5f3fc", stripe: "#0e7490" },
  { name: "Sky",     hex: "#bae6fd", stripe: "#0369a1" },
  { name: "Blue",    hex: "#bfdbfe", stripe: "#1d4ed8" },
  { name: "Indigo",  hex: "#c7d2fe", stripe: "#4338ca" },
  { name: "Violet",  hex: "#ddd6fe", stripe: "#6d28d9" },
  { name: "Purple",  hex: "#e9d5ff", stripe: "#7e22ce" },
  { name: "Fuchsia", hex: "#f5d0fe", stripe: "#a21caf" },
  { name: "Pink",    hex: "#fbcfe8", stripe: "#be185d" },
  { name: "Rose",    hex: "#fecdd3", stripe: "#be123c" },
] as const;

// Picker-grid order = colour-wheel order (the natural reading order of
// PALETTE). Used by openPalettePopup.
export const COLOR_PALETTE_BY_HUE: readonly PaletteEntry[] = PALETTE;

// Auto-assignment order. Interleaved warm/cool/warm/cool so the first
// few nextColor() picks look maximally distinct instead of three
// adjacent shades of blue.
// Index sequence into PALETTE that reproduces the prior interleaved
// auto-assignment order: red, sky, amber, violet, green, pink, teal,
// orange, indigo, emerald, fuchsia, yellow, blue, lime, purple, rose,
// cyan. Tested against the existing nextColor() tests.
const INTERLEAVE_INDEX = [0, 9, 2, 12, 5, 15, 7, 1, 11, 6, 14, 3, 10, 4, 13, 16, 8];
export const COLOR_PALETTE: readonly string[] = INTERLEAVE_INDEX.map((i) => PALETTE[i]!.hex);

// Hex → companion stripe colour lookup. Derived from PALETTE so a
// future palette edit can't accidentally leave a bar without its pair.
const STRIPE_PAIRS: Record<string, string> = Object.fromEntries(
  PALETTE.map((p) => [p.hex, p.stripe]),
);

// Hex → display name lookup (e.g. "#fecaca" → "Red"). Used by the
// picker popup's per-swatch tooltip.
const COLOR_NAMES: Record<string, string> = Object.fromEntries(
  PALETTE.map((p) => [p.hex, p.name]),
);
export function colorName(hex: string): string | null {
  return COLOR_NAMES[hex.toLowerCase()] ?? null;
}

export function nextColor(used: Record<string, string>): string {
  const usedSet = new Set(Object.values(used).map((c) => c.toLowerCase()));
  const fresh = COLOR_PALETTE.find((c) => !usedSet.has(c.toLowerCase()));
  if (fresh) return fresh;
  const fallbackIndex = Object.keys(used).length % COLOR_PALETTE.length;
  return COLOR_PALETTE[fallbackIndex]!;
}

export function loadPortColors(): Record<string, string> {
  return { ...getCache().portColors };
}

export function savePortColors(colors: Record<string, string>): void {
  setPortColors(colors);
}


// Brightness-aware text colour for legibility on the palette swatches.
export function textOn(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum < 0.6 ? "#fff" : "#1f2937";
}

export function darken(hex: string, factor: number): string {
  const r = Math.max(0, Math.round(parseInt(hex.slice(1, 3), 16) * (1 - factor)));
  const g = Math.max(0, Math.round(parseInt(hex.slice(3, 5), 16) * (1 - factor)));
  const b = Math.max(0, Math.round(parseInt(hex.slice(5, 7), 16) * (1 - factor)));
  return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
}

export function lighten(hex: string, factor: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const lift = (v: number) => Math.min(255, Math.round(v + (255 - v) * factor));
  return "#" + [lift(r), lift(g), lift(b)].map((v) => v.toString(16).padStart(2, "0")).join("");
}

// Exposed for unit tests and for the hatch / stripe contrast helpers.
// Reuses textOn's NTSC formula so both decisions share one luminance signal.
export function luminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

// WCAG-style perceptual luminance via sRGB linearisation. More
// perceptually accurate than NTSC for non-grey hues — NTSC over-weights
// green, mis-classifying cyan/teal colours like #63eedd as "dark" when
// they visibly read as light. Used by stripeColor + hatchVariant; the
// legacy NTSC luminance() above is kept for textOn's existing threshold.
function srgbLinear(c: number): number {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}
export function perceptualLuminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return 0.2126 * srgbLinear(r) + 0.7152 * srgbLinear(g) + 0.0722 * srgbLinear(b);
}

// Stevedore stripe colour. First-choice lookup against the curated
// STRIPE_PAIRS table — every COLOR_PALETTE entry has a designer-picked
// companion stripe (tailwind-200 → tailwind-700 in the same hue).
// Fallback for legacy user-picked custom colours: contrast-aware
// darken-or-lighten by 40% at perceptual luminance threshold 0.55.
export function stripeColor(hex: string): string {
  const paired = STRIPE_PAIRS[hex.toLowerCase()];
  if (paired) return paired;
  return perceptualLuminance(hex) > 0.55 ? darken(hex, 0.4) : lighten(hex, 0.4);
}

// Combined-bar hatch variant — pairs with .voyageItemCombined--{light,dark}
// CSS rules. 'dark' = dark-tinted hatch on light fills; 'light' = white
// hatch on dark fills. Same threshold as stripeColor so the two
// decisions stay in lockstep.
export function hatchVariant(hex: string): "light" | "dark" {
  return perceptualLuminance(hex) > 0.55 ? "dark" : "light";
}
